import React, { useState, useEffect } from 'react';
import {
  IonContent,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonList,
  IonItem,
  IonLabel,
  IonCheckbox,
  IonButton,
  IonIcon,
  IonAccordionGroup,
  IonAccordion,
  IonPage,
  IonRadio,
  IonRadioGroup,
  IonInput
} from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { AnnotationType, AnnotationStatus } from '../../plan/models/annotation.model';
import { AnnotationFilterView } from '../../plan/types/annotation.types';
import { close, searchOutline, closeOutline } from 'ionicons/icons';
import { useAnnotationFilters } from '../hooks/use-annotation-filters.hook';
import './annotation-filters.component.scss';

const ANNOTATION_TYPES: AnnotationType[] = ['MODIFICATION', 'CREATION', 'DELETION', 'INFO'];
const ANNOTATION_STATUSES: AnnotationStatus[] = ['NEW', 'TODO', 'IN_PROGRESS', 'PENDING', 'DONE', 'ARCHIVED'];
const DEFAULT_STATUSES: AnnotationStatus[] = ['TODO', 'IN_PROGRESS', 'NEW', 'PENDING'];

interface AnnotationFiltersProps {
  onClose?: () => void;
  onFilterChange?: (filter: AnnotationFilterView) => void;
  currentFilter?: AnnotationFilterView;
  currentSort?: string;
  onSortChange?: (sort: string) => void;
  onActiveFiltersCountChange?: (count: number) => void;
  hideSiteFilter?: boolean;
  availableAuthors?: string[];
  availablePortfolios?: string[];
  availableSites?: { id: string; name: string }[];
}

export const AnnotationFilters: React.FC<AnnotationFiltersProps> = ({ 
  onClose = () => {}, 
  onFilterChange,
  currentFilter = {
    siteIds: null,
    annotationTypes: null,
    annotationStatus: DEFAULT_STATUSES,
    hasAttachment: null,
    annotationAuthor: null,
    annotationPortfolios: null
  },
  currentSort = 'date_asc',
  onSortChange,
  onActiveFiltersCountChange,
  hideSiteFilter,
  availableAuthors = [],
  availablePortfolios = [],
  availableSites = []
}) => {
  const { t } = useTranslation();
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<string>(currentSort);
  const [siteSearch, setSiteSearch] = useState<string>('');
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const {
    selectedSites,
    setSelectedSites,
    selectedTypes,
    setSelectedTypes,
    selectedStatuses,
    setSelectedStatuses,
    hasAttachment,
    setHasAttachment,
    selectedAuthors,
    setSelectedAuthors,
    selectedPortfolios,
    setSelectedPortfolios,
    availableSites: sitesFromHook,
    availableAuthors: authorsFromHook,
    availablePortfolios: portfoliosFromHook,
    getSitesCount,
    getTypesCount,
    getStatusesCount,
    getAuthorsCount,
    getPortfoliosCount,
    getAttachmentCount
  } = useAnnotationFilters(onClose, onActiveFiltersCountChange, hideSiteFilter, availableAuthors, availablePortfolios, availableSites);

  // Initialiser les valeurs avec currentFilter
  useEffect(() => {
    if (currentFilter.siteIds) {
      setSelectedSites(currentFilter.siteIds);
    }
    if (currentFilter.annotationTypes) {
      setSelectedTypes(currentFilter.annotationTypes as AnnotationType[]);
    }
    if (currentFilter.annotationStatus) {
      setSelectedStatuses(currentFilter.annotationStatus as AnnotationStatus[]);
    }
    if (currentFilter.hasAttachment !== undefined) {
      setHasAttachment(currentFilter.hasAttachment);
    }
    if (currentFilter.annotationAuthor) {
      setSelectedAuthors(currentFilter.annotationAuthor as string[]);
    }
    if (currentFilter.annotationPortfolios) {
      setSelectedPortfolios(currentFilter.annotationPortfolios);
    }
  }, [currentFilter, setSelectedSites, setSelectedTypes, setSelectedStatuses, setHasAttachment, setSelectedAuthors, setSelectedPortfolios]);

  const filteredSites = sitesFromHook.filter(site => 
    site.name.toLowerCase().includes(siteSearch.toLowerCase())
  );

  const getSelectedFilters = (): AnnotationFilterView => {
    return {
      siteIds: selectedSites.length > 0 ? selectedSites : null,
      annotationTypes: selectedTypes.length > 0 ? selectedTypes : null,
      annotationStatus: selectedStatuses.length > 0 ? selectedStatuses : null,
      hasAttachment: hasAttachment,
      annotationAuthor: selectedAuthors.length > 0 ? selectedAuthors : null,
      annotationPortfolios: selectedPortfolios.length > 0 ? selectedPortfolios : null,
    };
  };

  const keepSectionOpen = (section: string) => {
    setExpandedSections((prev) =>
      prev.includes(section) ? prev : [...prev, section]
    );
  };

  const handleSiteChange = (siteId: string) => {
    setSelectedSites(prev =>
      prev.includes(siteId)
        ? prev.filter(id => id !== siteId)
        : [...prev, siteId]
    );
    keepSectionOpen('sites');
  };

  const handleTypeChange = (type: AnnotationType) => {
    setSelectedTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
    keepSectionOpen('types');
  };

  const handleStatusChange = (status: AnnotationStatus) => {
    setSelectedStatuses(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
    keepSectionOpen('statuses');
  };

  const handleAttachmentChange = (value: string | null) => {
    setHasAttachment(value);
    keepSectionOpen('attachment');
  };

  const handleAuthorChange = (author: string) => {
    setSelectedAuthors(prev =>
      prev.includes(author)
        ? prev.filter(t => t !== author)
        : [...prev, author]
    );
    keepSectionOpen('authors');
  };

  const handlePortfolioChange = (portfolio: string) => {
    setSelectedPortfolios(prev =>
      prev.includes(portfolio)
        ? prev.filter(p => p !== portfolio)
        : [...prev, portfolio]
    );
    keepSectionOpen('portfolios');
  };

  const handleSortChange = (value: string) => {
    setSortBy(value);
    if (onSortChange) {
      onSortChange(value);
    }
  };

  const handleApplyFilters = () => {
    const filters = getSelectedFilters();
    if (onFilterChange) {
      onFilterChange(filters);
    }
    onClose();
  };

  return (
    <>
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="end">
            <IonButton onClick={onClose}>
              <IonIcon slot="icon-only" icon={close} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent 
        scrollY={true}
        style={{
          '--padding-bottom': '80px'
        }}
      >
        <div className="filters-title">
          <h2>{t('annotations.filter.orderBy')}</h2>
        </div>
        <IonList className="sort-options">
          <IonRadioGroup value={sortBy} onIonChange={e => handleSortChange(e.detail.value)}>
            <IonItem button onClick={() => handleSortChange('date_desc')}>
              <IonLabel>{t('annotations.filter.orderByValues.dateDesc')}</IonLabel>
              <IonRadio slot="start" value="date_desc" />
            </IonItem>
            <IonItem button onClick={() => handleSortChange('date_asc')}>
              <IonLabel>{t('annotations.filter.orderByValues.dateAsc')}</IonLabel>
              <IonRadio slot="start" value="date_asc" />
            </IonItem>
            <IonItem button onClick={() => handleSortChange('site_code_asc')}>
              <IonLabel>{t('annotations.filter.orderByValues.siteCodeAsc')}</IonLabel>
              <IonRadio slot="start" value="site_code_asc" />
            </IonItem>
            <IonItem button onClick={() => handleSortChange('site_code_desc')}>
              <IonLabel>{t('annotations.filter.orderByValues.siteCodeDesc')}</IonLabel>
              <IonRadio slot="start" value="site_code_desc" />
            </IonItem>
          </IonRadioGroup>
        </IonList>

        <div className="filters-title">
          <h2>{t('annotations.filter.filters')}</h2>
        </div>
        <IonAccordionGroup
          multiple={true}
          value={expandedSections}
          onIonChange={(e) => {
            const newValue = e.detail.value;
            if (Array.isArray(newValue)) {
              setExpandedSections(newValue);
            } else if (typeof newValue === 'string') {
              setExpandedSections((prev) =>
                prev.includes(newValue) ? prev : [...prev, newValue]
              );
            }
          }}
        >
          {/* TYPES */}
          <IonAccordion value="types">
            <IonItem slot="header">
              <IonLabel>{t('annotations.filter.type')}{getTypesCount() > 0 ? ` (${getTypesCount()})` : ''}</IonLabel>
            </IonItem>
            <IonList slot="content">
              {ANNOTATION_TYPES.map(type => (
                <IonItem key={type} button={false} onClick={(e) => e.stopPropagation()}>
                  <IonCheckbox
                    checked={selectedTypes.includes(type)}
                    onClick={(e) => e.stopPropagation()}
                    onIonChange={() => handleTypeChange(type)}
                    labelPlacement="end"
                  >
                    {t(`plan.annotationForm.form.type.${type.toLowerCase()}`)}
                    <div className={`type-dot ${type.toLowerCase()}`} />
                  </IonCheckbox>
                </IonItem>
              ))}
            </IonList>
          </IonAccordion>

          {/* STATUSES */}
          <IonAccordion value="statuses">
            <IonItem slot="header">
              <IonLabel>{t('annotations.filter.status')}{getStatusesCount() > 0 ? ` (${getStatusesCount()})` : ''}</IonLabel>
            </IonItem>
            <IonList slot="content">
              {ANNOTATION_STATUSES.map(status => (
                <IonItem key={status} button={false} onClick={(e) => e.stopPropagation()}>
                  <IonCheckbox
                    checked={selectedStatuses.includes(status)}
                    onClick={(e) => e.stopPropagation()}
                    onIonChange={() => handleStatusChange(status)}
                    labelPlacement="end"
                  >
                    {t(`plan.annotationForm.form.status.${status}`)}
                  </IonCheckbox>
                </IonItem>
              ))}
            </IonList>
          </IonAccordion>

          {/* SITES */}
          {!hideSiteFilter && (
            <IonAccordion value="sites">
              <IonItem slot="header">
                <IonLabel>{t('annotations.filter.location')}{getSitesCount() > 0 ? ` (${getSitesCount()})` : ''}</IonLabel>
              </IonItem>
              <div slot="content" style={{ background: '#FFFFFF' }}>
                <div style={{ marginTop: '8px' }}>
                  <IonItem
                    lines="none"
                    style={{
                      width: 'calc(100% - 10px)',
                      maxWidth: '100%',
                      background: '#FFFFFF',
                      border: '1px solid #ccc',
                      borderRadius: '100px',
                      paddingLeft: '8px',
                      paddingRight: '8px',
                      alignItems: 'center',
                      height: '40px',
                      display: 'flex',
                      marginLeft: '5px',
                      marginRight: '5px',
                    }}
                  >
                    <IonIcon
                      icon={searchOutline}
                      slot="start"
                      style={{
                        fontSize: '16px',
                        color: !siteSearch ? '#888' : '#fff',
                        backgroundColor: !siteSearch
                          ? 'transparent'
                          : isFocused
                            ? '#165BE6'
                            : '#4B4D55',
                        borderRadius: '50%',
                        padding: '4px',
                        height: '24px',
                        width: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background-color 0.3s',
                        marginRight: '6px',
                        verticalAlign: 'middle',
                      }}
                    />
                    <IonInput
                      placeholder={t('annotations.filter.filterOnSiteFilter')}
                      value={siteSearch}
                      onIonInput={e => setSiteSearch(e.detail.value || '')}
                      onIonFocus={() => setIsFocused(true)}
                      onIonBlur={() => setIsFocused(false)}
                      style={{
                        font:'open-sans, sans-serif',
                        weight: '400',
                        background: 'transparent',
                        flex: 1,
                        fontSize: '12px',
                        lineHeight: '100%',
                        verticalAlign: 'middle',
                        height:'100%',
                        alignItems: 'center',
                      }}
                      clearInput={false}
                      role="search"
                      enterkeyhint="search"
                    />
                    {siteSearch && (
                      <IonButton
                        fill="clear"
                        size="small"
                        onClick={() => setSiteSearch('')}
                        style={{
                          padding: '2px',
                          backgroundColor: 'transparent',
                          boxShadow: 'none',
                          verticalAlign: 'middle',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        className="no-hover-bg"
                      >
                        <IonIcon
                          icon={closeOutline}
                          slot="icon-only"
                          style={{
                            fontSize: '14px',
                            color: '#888',
                            backgroundColor: 'transparent',
                          }}
                        />
                      </IonButton>
                    )}
                  </IonItem>
                </div>
                <IonList style={{ background: '#FFFFFF' }}>
                  {filteredSites.map(site => (
                    <IonItem key={site.id} button={false} onClick={(e) => e.stopPropagation()}>
                      <IonCheckbox
                        checked={selectedSites.includes(site.id)}
                        onClick={(e) => e.stopPropagation()}
                        onIonChange={() => handleSiteChange(site.id)}
                        labelPlacement="end"
                      >
                        {site.name}
                      </IonCheckbox>
                    </IonItem>
                  ))}
                </IonList>
              </div>
            </IonAccordion>
          )}

          {/* AUTHORS */}
          <IonAccordion value="authors">
            <IonItem slot="header">
              <IonLabel>{t('annotations.filter.author')}{getAuthorsCount() > 0 ? ` (${getAuthorsCount()})` : ''}</IonLabel>
            </IonItem>
            <IonList slot="content">
              {authorsFromHook.map(author => (
                <IonItem key={author} button={false} onClick={(e) => e.stopPropagation()}>
                  <IonCheckbox
                    checked={selectedAuthors.includes(author)}
                    onClick={(e) => e.stopPropagation()}
                    onIonChange={() => handleAuthorChange(author)}
                    labelPlacement="end"
                  >
                    {author || t('annotations.filter.unknownAuthor')}
                  </IonCheckbox>
                </IonItem>
              ))}
            </IonList>
          </IonAccordion>

          {/* PORTFOLIOS */}
          <IonAccordion value="portfolios">
            <IonItem slot="header">
              <IonLabel>{t('annotations.filter.portfolio')}{getPortfoliosCount() > 0 ? ` (${getPortfoliosCount()})` : ''}</IonLabel>
            </IonItem>
            <IonList slot="content">
              {portfoliosFromHook.map(portfolio => (
                <IonItem key={portfolio} button={false} onClick={(e) => e.stopPropagation()}>
                  <IonCheckbox
                    checked={selectedPortfolios.includes(portfolio)}
                    onClick={(e) => e.stopPropagation()}
                    onIonChange={() => handlePortfolioChange(portfolio)}
                    labelPlacement="end"
                  >
                    {portfolio || t('annotations.filter.unknownPortfolio')}
                  </IonCheckbox>
                </IonItem>
              ))}
            </IonList>
          </IonAccordion>

          {/* ATTACHMENT */}
          <IonAccordion value="attachment">
            <IonItem slot="header">
              <IonLabel>{t('annotations.filter.attachment')}{getAttachmentCount() > 0 ? ` (${getAttachmentCount()})` : ''}</IonLabel>
            </IonItem>
            <IonList slot="content">
              <IonItem button={false} onClick={(e) => e.stopPropagation()}>
                <IonCheckbox
                  checked={hasAttachment === 'true'}
                  onClick={(e) => e.stopPropagation()}
                  onIonChange={() => handleAttachmentChange(hasAttachment === 'true' ? null : 'true')}
                  labelPlacement="end"
                >
                  {t('annotations.filter.attachmentValues.yes')}
                </IonCheckbox>
              </IonItem>
              <IonItem button={false} onClick={(e) => e.stopPropagation()}>
                <IonCheckbox
                  checked={hasAttachment === 'false'}
                  onClick={(e) => e.stopPropagation()}
                  onIonChange={() => handleAttachmentChange(hasAttachment === 'false' ? null : 'false')}
                  labelPlacement="end"
                >
                  {t('annotations.filter.attachmentValues.no')}
                </IonCheckbox>
              </IonItem>
            </IonList>
          </IonAccordion>
        </IonAccordionGroup>
      </IonContent>
    </IonPage>
    <div className="filters-footer">
    <IonButton expand="block" onClick={handleApplyFilters}>
      {t('annotations.filter.validate')}
    </IonButton>
  </div>
  </>
  );
};
